import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, query, where, doc, deleteDoc, updateDoc, addDoc, serverTimestamp, getDocs, writeBatch } from 'firebase/firestore';
import { getStorage } from "firebase/storage";

// Componentes Modulares
import KanbanView from '../components/CRM/KanbanView';
import TasksInboxView from '../components/CRM/TasksInboxView';
import LeadDetailModal from '../components/CRM/LeadDetailModal';
import LeadEditModal from '../components/CRM/LeadEditModal';
import DeleteConfirmationModal from '../components/CRM/DeleteConfirmationModal';
import CRMDashboardMetrics from '../components/CRM/CRMDashboardMetrics';
// AF&B F1 (punto 5, hallazgo H3 del F0) — AIAssistant queda OCULTO, no borrado.
// Llama a la callable `askPackagingAI`, que NO existe (ni en functions/index.js ni entre las
// funciones desplegadas): cada envio fallaba al 100%. Ademas su panel abierto es
// `fixed right-6 w-full`, que en 390px desborda 24px y mete scroll horizontal.
// El componente se conserva en src/components/CRM/AIAssistant.js para arreglarlo despues del
// show; aqui solo se retira del render. Para revivirlo: descomentar el import y el <AIAssistant/>.
// import AIAssistant from '../components/CRM/AIAssistant';

const IconUserPlus = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path></svg>;

const TradeshowCRMView = ({ user, db }) => {
    const [activeView, setActiveView] = useState('kanban');
    const [allLeads, setAllLeads] = useState([]);
    const [columns, setColumns] = useState(null); 
    const [isLoading, setIsLoading] = useState(true);
    const [leadToEdit, setLeadToEdit] = useState(null); 
    const [selectedLead, setSelectedLead] = useState(null);
    const [sellerFilter, setSellerFilter] = useState('all');
    const [leadToDelete, setLeadToDelete] = useState(null);

    const isAdmin = user.permissions?.canManageRoles;

    useEffect(() => {
        if (!user || !user.uid) return;
        setIsLoading(true);
        let q = isAdmin 
            ? query(collection(db, "tradeshow_leads")) 
            : query(collection(db, "tradeshow_leads"), where("sellerId", "==", user.uid));
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setAllLeads(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => { console.error("Error fetching leads:", error); setIsLoading(false); });
        return () => unsubscribe();
    }, [db, user, isAdmin]);

    const filteredLeads = useMemo(() => {
        const activeLeads = allLeads.filter(lead => lead.status !== 'converted');
        if (sellerFilter === 'all') return activeLeads;
        return activeLeads.filter(lead => lead.sellerId === sellerFilter);
    }, [allLeads, sellerFilter]);
    
    useEffect(() => {
        const initialColumns = {'new': { id: 'new', title: 'Nuevo Lead', leads: [] },'contacted': { id: 'contacted', title: 'Contactado', leads: [] },'qualified': { id: 'qualified', title: 'Calificado', leads: [] },'won': { id: 'won', title: 'Ganado', leads: [] },'lost': { id: 'lost', title: 'Perdido', leads: [] },};
        filteredLeads.forEach(lead => {
            const columnId = lead.status || 'new';
            if (initialColumns[columnId]) initialColumns[columnId].leads.push(lead);
        });
        setColumns(initialColumns);
    }, [filteredLeads]);

    const onDragEnd = async (result) => {
        const { destination, source, draggableId } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }
        
        try {
            const leadRef = doc(db, "tradeshow_leads", draggableId);
            await updateDoc(leadRef, { status: destination.droppableId });
            
            const endColumnTitle = columns[destination.droppableId]?.title || destination.droppableId;
            const timelineRef = collection(db, `tradeshow_leads/${draggableId}/timeline`);
            await addDoc(timelineRef, { type: 'log', text: `Estado cambiado a: ${endColumnTitle}.`, authorName: "Sistema", authorId: "system", timestamp: serverTimestamp() });
        } catch (error) {
            console.error("Error updating lead status:", error);
        }
    };

    const handleConvertLead = async (leadToConvert) => {
        if (!window.confirm(`¿Estás seguro de que quieres convertir a "${leadToConvert.company}" en un cliente?`)) {
            return;
        }

        try {
            const clientsRef = collection(db, "clients");
            const q = query(clientsRef, where("name", "==", leadToConvert.company));
            const existingClients = await getDocs(q);

            if (!existingClients.empty) {
                alert(`El cliente "${leadToConvert.company}" ya existe. No se creará un duplicado.`);
                const leadRef = doc(db, "tradeshow_leads", leadToConvert.id);
                await updateDoc(leadRef, { status: 'converted' });
                return;
            }

            const batch = writeBatch(db);
            const newClientRef = doc(collection(db, "clients"));
            
            // --- MEJORA: Asignar vendedor al nuevo cliente ---
            batch.set(newClientRef, {
                name: leadToConvert.company,
                contacts: [{
                    name: leadToConvert.contactName,
                    email: leadToConvert.email || '',
                    phone: leadToConvert.phone || '',
                    isPrimary: true,
                }],
                sellerId: leadToConvert.sellerId,
                sellerName: leadToConvert.sellerName,
                createdBy: user.uid,
                createdAt: serverTimestamp(),
            });

            const leadRef = doc(db, "tradeshow_leads", leadToConvert.id);
            batch.update(leadRef, { status: 'converted' });
            
            await batch.commit();
            alert(`¡Éxito! El lead "${leadToConvert.company}" ha sido convertido a cliente y asignado a ${leadToConvert.sellerName}.`);

        } catch (error) {
            console.error("Error al convertir el lead:", error);
            alert("Ocurrió un error al convertir el lead. Revisa la consola.");
        }
    };

    const handleLeadSave = useCallback(() => setLeadToEdit(null), []);
    
    const handleConfirmDelete = async () => {
        if (!leadToDelete) return;
        try {
            await deleteDoc(doc(db, "tradeshow_leads", leadToDelete.id));
            setLeadToDelete(null);
            setSelectedLead(null);
        } catch (error) { console.error("Error deleting lead: ", error); }
    };
    
    // AF&B F1 — Los leads del kiosko nacen SIN `sellerId` (todavia no tienen vendedor).
    // Sin el filter, cada uno metia una <option> con key y value `undefined`: React avisaba
    // por la key duplicada y, si el admin elegia "Desconocido", el filtro se quedaba en cero
    // leads. El selector lista SOLO vendedores reales; los leads sin asignar se ven en "Todos".
    const uniqueSellers = useMemo(() => ([...new Map(allLeads
        .filter(lead => lead.sellerId)
        .map(lead => [lead.sellerId, {id: lead.sellerId, name: lead.sellerName || 'Desconocido'}])).values()]), [allLeads]);

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <h1 className="text-3xl font-bold text-gray-800">Sales Hub</h1>
                    <div className="bg-gray-200 p-1 rounded-lg">
                        <button onClick={() => setActiveView('kanban')} className={`px-3 py-1 text-sm font-semibold rounded-md ${activeView === 'kanban' ? 'bg-white text-blue-600 shadow' : 'text-gray-600'}`}>Kanban</button>
                        <button onClick={() => setActiveView('tasks')} className={`px-3 py-1 text-sm font-semibold rounded-md ${activeView === 'tasks' ? 'bg-white text-blue-600 shadow' : 'text-gray-600'}`}>Bandeja de Tareas</button>
                    </div>
                </div>
                <div className="flex items-center gap-4 w-full md:w-auto">
                    {isAdmin && (<select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)} className="p-2 border rounded-lg bg-white shadow-sm"><option value="all">Todos los Vendedores</option>{uniqueSellers.map(seller => (<option key={seller.id} value={seller.id}>{seller.name}</option>))}</select>)}
                    <button onClick={() => setLeadToEdit({})} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-md transition-all w-full md:w-auto justify-center"><IconUserPlus /><span>Capturar Lead</span></button>
                </div>
            </div>
            
            {isLoading ? (<div className="text-center py-10">Cargando datos...</div>) : (
                <>
                    {activeView === 'kanban' && <CRMDashboardMetrics leads={filteredLeads} />}
                    <div style={{ display: activeView === 'kanban' ? 'block' : 'none' }}>
                        <KanbanView columns={columns} onDragEnd={onDragEnd} onSelectLead={setSelectedLead} onConvertLead={handleConvertLead} isAdmin={isAdmin} />
                    </div>
                    <div style={{ display: activeView === 'tasks' ? 'block' : 'none' }}>
                        <TasksInboxView db={db} user={user} sellerFilter={sellerFilter} onSelectLead={setSelectedLead} />
                    </div>
                </>
            )}

            {leadToEdit && <LeadEditModal db={db} user={user} onClose={() => setLeadToEdit(null)} onSave={handleLeadSave} initialLeadData={leadToEdit} />}
            {selectedLead && <LeadDetailModal lead={selectedLead} columns={columns} db={db} storage={getStorage()} user={user} onClose={() => setSelectedLead(null)} onEdit={() => { setSelectedLead(null); setLeadToEdit(selectedLead); }} onDelete={() => setLeadToDelete(selectedLead)} />}
            {leadToDelete && <DeleteConfirmationModal onConfirm={handleConfirmDelete} onCancel={() => setLeadToDelete(null)} leadName={leadToDelete.contactName} />}
            {/* <AIAssistant selectedLead={selectedLead} />  — ver nota del import (H3) */}
        </div>
    );
};

export default TradeshowCRMView;

